import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Eye, Upload, Image, Bold, Italic, Link, List, AlignLeft, AlignCenter, Code, Quote, Heading1, Heading2, Trash2, Edit3, Search, Filter, Calendar, Tag, User, Globe, FileText, Settings, X, Check, AlertCircle } from 'lucide-react';
import { API_BASE_URL as CONFIG_API_BASE_URL, API_KEY } from '../../config/api';
const WINNEST_API_KEY = process.env.REACT_APP_WINNEST_API_KEY || API_KEY;
console.log('🔐 API Keys configured:', {
  SIRIDHARINI: API_KEY,
  WINNEST: WINNEST_API_KEY,
  ENV_VAR_EXISTS: !!process.env.REACT_APP_WINNEST_API_KEY
});

const SiridhariniBlogAdminUpload = () => {
  const { id } = useParams(); // Get blog ID from URL if editing
  const navigate = useNavigate(); // For navigation
  const [activeTab, setActiveTab] = useState('list');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);

  // Import centralized API configuration
  const API_BASE_URL_ROOT = CONFIG_API_BASE_URL;
  const API_BASE_URL = `${API_BASE_URL_ROOT}/siridharini-blogs`;
  const UPLOAD_API_URL = `${API_BASE_URL_ROOT}/blog-uploads`;

  const [blogData, setBlogData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featuredImageUrl: '',
    category: '',
    tags: [],
    status: 'DRAFT',
    author: 'SiriDharini Team',
    seoTitle: '',
    seoDescription: '',
    bgColor: '',
    iconName: 'FileText',
    displayOrder: 0,
    isActive: true,
    readTime: '',
    isFeatured: false
  });
  
  const [images, setImages] = useState([]);
  const [showImageManager, setShowImageManager] = useState(false);
  const [showPdfManager, setShowPdfManager] = useState(false);
  const [pdfs, setPdfs] = useState([]);
  const [editorContent, setEditorContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  
  const fileInputRef = useRef(null);
  const featuredImageRef = useRef(null);
  const editorRef = useRef(null);
  const isFormattingRef = useRef(false);
  
  // Helper function to get auth headers
  const getAuthHeaders = (url = '') => {
    const token = localStorage.getItem('authToken');
    // For blog-uploads endpoints, use WinNest API key as these are shared endpoints
    // For SiriDharini-specific endpoints, use SiriDharini API key
    const apiKey = url.includes('blog-uploads') ? WINNEST_API_KEY : API_KEY;
    
    console.log('🔑 Getting auth headers:', {
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 50) + '...' : 'No token',
      apiKey: apiKey,
      url: url
    });
    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key': apiKey
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    console.log('📤 Headers being sent:', headers);
    return headers;
  };
  
  // Helper function for multipart headers
  const getMultipartHeaders = (url = '') => {
    const token = localStorage.getItem('authToken');
    // For blog-uploads endpoints, use WinNest API key as these are shared endpoints
    // For SiriDharini-specific endpoints, use SiriDharini API key
    const apiKey = url.includes('blog-uploads') ? WINNEST_API_KEY : API_KEY;
    
    console.log('🔑 Getting multipart headers:', {
      hasToken: !!token,
      apiKey: apiKey,
      url: url
    });
    const headers = {
      'X-API-Key': apiKey
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    console.log('📤 Multipart headers being sent:', headers);
    return headers;
  };

  // Debounced content update
  const debouncedContentUpdate = useCallback(
    (() => {
      let timeoutId;
      return (content) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (!isFormattingRef.current) {
            setEditorContent(content);
          }
        }, 300);
      };
    })(),
    []
  );

  // Fix text direction in editor
  const fixTextDirection = useCallback(() => {
    if (editorRef.current) {
      const editor = editorRef.current;
      editor.style.direction = 'ltr';
      editor.style.textAlign = 'left';
      editor.style.unicodeBidi = 'normal';
      editor.setAttribute('dir', 'ltr');
      
      const allElements = editor.querySelectorAll('*');
      allElements.forEach(element => {
        element.style.direction = 'ltr';
        element.style.textAlign = 'left';
        element.style.unicodeBidi = 'normal';
        element.setAttribute('dir', 'ltr');
      });
    }
  }, []);

  // Enhanced format text function
  const formatText = useCallback((command, value = null) => {
    if (!editorRef.current) return;

    isFormattingRef.current = true;
    
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    
    editorRef.current.focus();
    
    if (range) {
      selection.removeAllRanges();
      selection.addRange(range);
    }

    try {
      if (command === 'formatBlock') {
        document.execCommand(command, false, value);
      } else if (command === 'createLink') {
        const url = prompt('Enter URL:');
        if (url) {
          document.execCommand('createLink', false, url);
        }
      } else {
        document.execCommand(command, false, value);
      }
    } catch (error) {
      console.error('Format command error:', error);
    }

    setTimeout(() => {
      fixTextDirection();
      if (editorRef.current) {
        const newContent = editorRef.current.innerHTML;
        setEditorContent(newContent);
      }
      isFormattingRef.current = false;
    }, 100);
  }, [fixTextDirection]);

  // Handle editor content change
  const handleEditorInput = useCallback((e) => {
    if (isFormattingRef.current) {
      return;
    }
    
    const content = e.target.innerHTML;
    fixTextDirection();
    debouncedContentUpdate(content);
  }, [fixTextDirection, debouncedContentUpdate]);

  // Handle editor events
  const handleEditorFocus = useCallback(() => {
    setTimeout(() => {
      fixTextDirection();
    }, 50);
  }, [fixTextDirection]);

  const handleEditorKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      setTimeout(() => {
        fixTextDirection();
      }, 50);
    }
  }, [fixTextDirection]);

  const handleEditorPaste = useCallback((e) => {
    e.preventDefault();
    
    const paste = (e.clipboardData || window.clipboardData).getData('text/plain');
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      
      const textNode = document.createTextNode(paste);
      range.insertNode(textNode);
      
      range.setStartAfter(textNode);
      range.setEndAfter(textNode);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    setTimeout(() => {
      fixTextDirection();
      if (editorRef.current) {
        setEditorContent(editorRef.current.innerHTML);
      }
    }, 100);
  }, [fixTextDirection]);

  // Insert image into editor
  const insertImageIntoEditor = useCallback((image) => {
    if (!editorRef.current) {
      return;
    }

    isFormattingRef.current = true;

    try {
      const imageHtml = `
        <figure class="blog-image-wrapper" style="
          margin: 20px 0; 
          text-align: center; 
          display: inline-block; 
          width: auto; 
          vertical-align: top;
          margin-right: 15px;
          margin-bottom: 15px;
        ">
          <img 
            src="${image.url}" 
            alt="${image.alt}" 
            class="blog-image-resizable"
            style="
              max-width: 300px; 
              height: auto; 
              border-radius: 12px; 
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              cursor: pointer;
              transition: transform 0.2s ease;
            " 
            onclick="this.classList.toggle('blog-image-expanded')"
          />
          <figcaption 
            contenteditable="true"
            style="
              margin-top: 8px; 
              font-size: 12px; 
              color: #666; 
              font-style: italic;
              max-width: 300px;
            "
          >
            ${image.alt || 'Add caption...'}
          </figcaption>
        </figure>`;
      
      const currentContent = editorRef.current.innerHTML;
      const newContent = currentContent + imageHtml + '<br>';
      
      editorRef.current.innerHTML = newContent;
      setEditorContent(newContent);
      
      editorRef.current.focus();
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.blur();
          editorRef.current.focus();
        }
      }, 100);
      
      setShowImageManager(false);
      setMessage({ type: 'success', text: 'Image inserted successfully!' });
      
    } catch (error) {
      console.error('Error inserting image:', error);
      setMessage({ type: 'error', text: 'Failed to insert image' });
    } finally {
      setTimeout(() => {
        isFormattingRef.current = false;
      }, 200);
    }
  }, [fixTextDirection]);

  // Update editor content when blogData changes
  useEffect(() => {
    if (editorRef.current && blogData.content !== editorContent) {
      editorRef.current.innerHTML = blogData.content || '';
      setEditorContent(blogData.content || '');
      fixTextDirection();
    }
  }, [blogData.content, fixTextDirection]);

  // Continuous direction fix
  useEffect(() => {
    const interval = setInterval(() => {
      if (editorRef.current && document.activeElement === editorRef.current) {
        fixTextDirection();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [fixTextDirection]);

  // Check if we're in edit mode from URL parameter
  useEffect(() => {
    if (id) {
      // If there's an ID in URL, switch to editor tab and load the blog
      setActiveTab('editor');
      handleEditById(id);
    } else {
      // No ID in URL, make sure we're on the list tab
      setActiveTab('list');
    }
  }, [id]);

  // Fetch all blogs for admin
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching blogs from:', `${API_BASE_URL}/admin`);
      
      const headers = getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/admin`, {
        method: 'GET',
        headers: headers,
      });

      console.log('📡 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Blogs fetched successfully:', data.length || 0, 'blogs');
        setBlogs(Array.isArray(data) ? data : []);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to fetch blogs:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching Siridharini blogs:', error);
      setMessage({ type: 'error', text: 'Failed to fetch blogs. Please check if the backend is running.' });
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch metadata
  const fetchMetadata = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/public/categories`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const categories = await response.json();
        setCategories(categories || []);
      }
      
      const authorsResponse = await fetch(`${API_BASE_URL}/public/authors`, {
        headers: getAuthHeaders()
      });
      if (authorsResponse.ok) {
        const authors = await authorsResponse.json();
        setAuthors(authors || []);
      }
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  // Load images from database
  const loadImages = async () => {
    try {
      const response = await fetch(`${UPLOAD_API_URL}/list/content`, {
        headers: getAuthHeaders(`${UPLOAD_API_URL}/list/content`)
      });
      
      if (response.ok) {
        const data = await response.json();
        
        const transformedImages = data.images.map(img => ({
          id: img.id || img.filename,
          url: img.downloadUri || img.url,
          name: img.originalFilename || img.filename,
          alt: img.altText || (img.originalFilename || img.filename || '').split('.')[0],
          size: img.size ? Math.round(img.size / 1024) + ' KB' : 'Unknown',
          filename: img.filename,
          contentType: img.contentType || 'image/jpeg',
          caption: img.caption || '',
          uploadedAt: img.uploadedAt || img.lastModified
        }));
        
        setImages(transformedImages);
      }
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  // Handle PDF upload
  const handlePdfUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${UPLOAD_API_URL}/pdf`, {
        method: 'POST',
        headers: getMultipartHeaders(`${UPLOAD_API_URL}/pdf`),
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        const newPdf = {
          id: result.id || Date.now(),
          name: result.originalFilename,
          filename: result.filename,
          size: Math.round(result.size / 1024) + ' KB',
          pages: result.totalPages || 'Unknown',
          extractedText: result.content || result.extractedText,
          downloadUrl: result.downloadUri || result.url || result.pdfUrl
        };
        
        setPdfs(prev => [...prev, newPdf]);
        
        // Automatically insert PDF into editor (like WinNest)
        insertPdfIntoEditor(newPdf);
        
        setMessage({ type: 'success', text: 'PDF inserted into blog!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to upload PDF' });
      }
    } catch (error) {
      console.error('Error uploading PDF:', error);
      setMessage({ type: 'error', text: 'Error uploading PDF' });
    }
  };

  // Insert PDF into editor (like WinNest)
  const insertPdfIntoEditor = useCallback((pdf) => {
    if (!editorRef.current) return;
    
    // Create PDF embed wrapper
    const pdfWrapper = document.createElement('div');
    pdfWrapper.className = 'blog-pdf-embed';
    pdfWrapper.contentEditable = 'false';
    pdfWrapper.style.cssText = 'margin: 20px 0; padding: 20px; background: #f8f9fa; border-radius: 8px; border: 1px solid #e2e8f0; position: relative;';
    
    // Create PDF header with icon and buttons only (no name)
    const pdfHeader = document.createElement('div');
    pdfHeader.style.cssText = 'display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;';
    
    const pdfInfo = document.createElement('div');
    pdfInfo.style.cssText = 'display: flex; align-items: center; gap: 10px;';
    
    const pdfIcon = document.createElement('span');
    pdfIcon.innerHTML = '📄';
    pdfIcon.style.cssText = 'font-size: 24px;';
    
    // Create buttons container
    const buttonsDiv = document.createElement('div');
    buttonsDiv.style.cssText = 'display: flex; gap: 10px;';
    
    // Open in New Tab button
    const openBtn = document.createElement('a');
    openBtn.href = pdf.downloadUrl || '#';
    openBtn.target = '_blank';
    openBtn.rel = 'noopener noreferrer';
    openBtn.textContent = '🔗 Open in New Tab';
    openBtn.style.cssText = 'padding: 8px 16px; background: #3b82f6; color: white; text-decoration: none; border-radius: 6px; font-size: 14px; cursor: pointer;';
    
    // Download PDF button
    const downloadBtn = document.createElement('a');
    downloadBtn.href = pdf.downloadUrl || '#';
    downloadBtn.download = pdf.name || 'document.pdf';
    downloadBtn.textContent = '⬇️ Download PDF';
    downloadBtn.style.cssText = 'padding: 8px 16px; background: #22c55e; color: white; text-decoration: none; border-radius: 6px; font-size: 14px; cursor: pointer;';
    
    pdfInfo.appendChild(pdfIcon);
    
    buttonsDiv.appendChild(openBtn);
    buttonsDiv.appendChild(downloadBtn);
    
    pdfHeader.appendChild(pdfInfo);
    pdfHeader.appendChild(buttonsDiv);
    
    // Create iframe for PDF preview
    const iframe = document.createElement('iframe');
    iframe.src = (pdf.downloadUrl || '') + '#toolbar=0';
    iframe.style.cssText = 'width: 100%; height: 600px; border: 1px solid #e2e8f0; border-radius: 6px; background: white;';
    iframe.title = pdf.name || 'PDF Document';
    
    // Add remove button
    const removeBtn = document.createElement('button');
    removeBtn.innerHTML = '×';
    removeBtn.style.cssText = `
      position: absolute;
      top: 10px;
      right: 10px;
      background: #ef4444;
      color: white;
      border: none;
      border-radius: 50%;
      width: 30px;
      height: 30px;
      font-size: 20px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
    `;
    removeBtn.onclick = function() {
      pdfWrapper.remove();
    };
    
    pdfWrapper.appendChild(removeBtn);
    pdfWrapper.appendChild(pdfHeader);
    pdfWrapper.appendChild(iframe);
    
    // Insert at cursor position
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(pdfWrapper);
      
      // Add a paragraph after PDF for continued editing
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      p.contentEditable = 'true';
      pdfWrapper.parentNode.insertBefore(p, pdfWrapper.nextSibling);
      
      // Move cursor to the new paragraph
      const newRange = document.createRange();
      newRange.selectNodeContents(p);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
      
      // Focus the editor
      editorRef.current.focus();
    } else {
      // If no selection, append to end
      editorRef.current.appendChild(pdfWrapper);
      
      // Add paragraph for continued typing
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      editorRef.current.appendChild(p);
      
      // Set cursor to the new paragraph
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(p);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
      editorRef.current.focus();
    }
    
    // Update content state
    setTimeout(() => {
      if (editorRef.current) {
        const content = editorRef.current.innerHTML;
        setEditorContent(content);
        setBlogData(prev => ({ ...prev, content }));
      }
    }, 100);
    
    // Close PDF manager if open
    setShowPdfManager(false);
    setMessage({ type: 'success', text: 'PDF inserted into blog!' });
  }, []);
  
  // Create blog from PDF (old method kept for backward compatibility)
  const createBlogFromPdf = (pdf) => {
    // Process and format PDF content
    let pdfContent = pdf.extractedText || '<p>PDF content will be displayed here</p>';
    
    // Format the content properly
    if (pdfContent && !pdfContent.includes('<')) {
      // If it's plain text, convert to HTML with proper formatting
      const lines = pdfContent.split('\n').filter(line => line.trim());
      
      // Format as a structured document
      let formattedContent = '<div class="pdf-content">';
      
      // Add title if available
      formattedContent += `<h2>${pdf.name.replace('.pdf', '')}</h2>`;
      
      // Process each line
      let inTable = false;
      let tableContent = [];
      
      lines.forEach((line) => {
        line = line.trim();
        if (line) {
          // Check if line looks like table data (contains multiple data points separated by spaces/tabs)
          const hasMultipleColumns = line.split(/\s{2,}|\t/).filter(col => col.trim()).length > 2;
          
          // Check if line looks like a header
          if (line.length < 80 && !hasMultipleColumns && 
              (line.includes('Date') || line.includes('Transaction') || 
               line.includes('Reference') || line.includes('Amount') || 
               line.includes('Product') || line.includes('Details'))) {
            // End table if we were in one
            if (inTable && tableContent.length > 0) {
              formattedContent += formatAsTable(tableContent);
              tableContent = [];
              inTable = false;
            }
            formattedContent += `<h3>${line}</h3>`;
          } else if (line.startsWith('---') || line.startsWith('===')) {
            // End table if we were in one
            if (inTable && tableContent.length > 0) {
              formattedContent += formatAsTable(tableContent);
              tableContent = [];
              inTable = false;
            }
            formattedContent += '<hr/>';
          } else if (hasMultipleColumns) {
            // This looks like table data
            inTable = true;
            tableContent.push(line);
          } else {
            // Regular paragraph
            if (inTable && tableContent.length > 0) {
              formattedContent += formatAsTable(tableContent);
              tableContent = [];
              inTable = false;
            }
            formattedContent += `<p>${line}</p>`;
          }
        }
      });
      
      // Handle any remaining table content
      if (inTable && tableContent.length > 0) {
        formattedContent += formatAsTable(tableContent);
      }
      
      // Helper function to format table data
      function formatAsTable(rows) {
        let table = '<table class="pdf-table"><tbody>';
        rows.forEach(row => {
          const cells = row.split(/\s{2,}|\t/).filter(cell => cell.trim());
          table += '<tr>';
          cells.forEach(cell => {
            table += `<td>${cell.trim()}</td>`;
          });
          table += '</tr>';
        });
        table += '</tbody></table>';
        return table;
      }
      
      formattedContent += '</div>';
      pdfContent = formattedContent;
    }
    
    // Clean up any problematic content
    pdfContent = pdfContent.replace(/<script[^>]*>.*?<\/script>/gi, '');
    pdfContent = pdfContent.replace(/<style[^>]*>.*?<\/style>/gi, '');
    
    setBlogData(prev => ({
      ...prev,
      title: pdf.name.replace('.pdf', ''),
      excerpt: 'Document imported from PDF',
      content: pdfContent,
      author: 'SiriDharini Team',
      readTime: `${Math.ceil((pdfContent.length / 5) / 200)} MIN READ`,
      category: 'DOCUMENTS'
    }));

    setEditorContent(pdfContent);
    
    if (editorRef.current) {
      editorRef.current.innerHTML = pdfContent;
      setTimeout(() => {
        fixTextDirection();
      }, 100);
    }

    setShowPdfManager(false);
    setMessage({ 
      type: 'success', 
      text: 'Blog created from PDF! Content preserved. Ready to publish!' 
    });
  };

  // Load initial data
  useEffect(() => {
    fetchBlogs();
    fetchMetadata();
    loadImages();
  }, []);

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Generate slug from title
  const generateSlug = async (title) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/generate-slug`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title })
      });

      if (response.ok) {
        const data = await response.json();
        return data.slug;
      }
    } catch (error) {
      console.error('Error generating slug:', error);
    }
    
    // Fallback slug generation
    return title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Handle form submission
  const handleSubmit = async (status = blogData.status) => {
    if (!blogData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    if (!editorContent.trim() && !blogData.content.trim()) {
      setMessage({ type: 'error', text: 'Content is required' });
      return;
    }

    setSubmitting(true);
    
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_BASE_URL}/admin/${editingId}` : `${API_BASE_URL}/admin`;

      let slug = blogData.slug;
      if (!slug.trim()) {
        slug = await generateSlug(blogData.title);
      }

      const requestData = {
        title: blogData.title,
        slug: slug,
        excerpt: blogData.excerpt,
        content: editorContent || blogData.content,
        featuredImageUrl: blogData.featuredImageUrl,
        category: blogData.category,
        tags: blogData.tags,
        status: status,
        author: blogData.author,
        seoTitle: blogData.seoTitle,
        seoDescription: blogData.seoDescription,
        displayOrder: blogData.displayOrder || 0,
        isActive: blogData.isActive,
        readTime: blogData.readTime,
        bgColor: blogData.bgColor,
        iconName: blogData.iconName,
        isFeatured: blogData.isFeatured
      };

      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: editingId ? 'Siridharini blog updated successfully!' : 'Siridharini blog created successfully!' 
        });
        resetForm();
        await fetchBlogs();
        setActiveTab('list');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ 
          type: 'error', 
          text: errorData.message || `Failed to save blog (${response.status})` 
        });
      }
    } catch (error) {
      console.error('Error saving Siridharini blog:', error);
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Siridharini blog?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Siridharini blog deleted successfully!' });
        await fetchBlogs();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.message || 'Failed to delete blog' });
      }
    } catch (error) {
      console.error('Error deleting Siridharini blog:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/${id}/toggle-status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Status updated successfully!' });
        await fetchBlogs();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.message || 'Failed to update status' });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Handle edit (navigate to edit route)
  const handleEdit = async (blog) => {
    console.log('✏️ Navigating to edit page for blog:', blog.id);
    navigate(`/adminportal/siridharini-blog-upload/edit/${blog.id}`);
  };

  // Handle edit by ID (for URL routing)
  const handleEditById = async (blogId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/${blogId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const fullBlog = await response.json();
        console.log('🔍 Loaded blog data for editing via URL:', fullBlog);
        console.log('📸 Featured image URL:', fullBlog.featuredImageUrl);
        console.log('🖼️ Content images:', fullBlog.images);
        setBlogData({
          title: fullBlog.title || '',
          slug: fullBlog.slug || '',
          excerpt: fullBlog.excerpt || '',
          content: fullBlog.content || '',
          featuredImageUrl: fullBlog.featuredImageUrl || '',
          category: fullBlog.category || '',
          tags: fullBlog.tags || [],
          status: fullBlog.status || 'DRAFT',
          author: fullBlog.author || 'SiriDharini Team',
          seoTitle: fullBlog.seoTitle || '',
          seoDescription: fullBlog.seoDescription || '',
          bgColor: fullBlog.bgColor || '',
          iconName: fullBlog.iconName || 'FileText',
          displayOrder: fullBlog.displayOrder || 0,
          isActive: fullBlog.isActive !== undefined ? fullBlog.isActive : true,
          readTime: fullBlog.readTime || '',
          isFeatured: fullBlog.isFeatured || false
        });
        
        // Load existing images for this blog
        if (fullBlog.images && fullBlog.images.length > 0) {
          const existingImages = fullBlog.images.map((imageUrl, index) => ({
            id: `existing_${Date.now()}_${index}`,
            url: imageUrl,
            name: `Image ${index + 1}`,
            size: 'Unknown',
            alt: `${fullBlog.title} - Image ${index + 1}`,
            isExisting: true
          }));
          setImages(existingImages);
        } else {
          setImages([]);
        }
        
        setEditorContent(fullBlog.content || '');
        setEditingId(blogId);
        setMessage({ type: '', text: '' });
        
        setTimeout(() => {
          if (editorRef.current) {
            editorRef.current.innerHTML = fullBlog.content || '';
            fixTextDirection();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching blog for edit via URL:', error);
      setMessage({ type: 'error', text: 'Failed to load blog for editing' });
      // Navigate back to list if there's an error
      navigate('/adminportal/siridharini-blog-upload');
    }
  };

  // Reset form
  const resetForm = () => {
    setBlogData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featuredImageUrl: '',
      category: '',
      tags: [],
      status: 'DRAFT',
      author: 'SiriDharini Team',
      seoTitle: '',
      seoDescription: '',
      bgColor: '',
      iconName: 'FileText',
      displayOrder: 0,
      isActive: true,
      readTime: '',
      isFeatured: false
    });
    setEditorContent('');
    setEditingId(null);
    setImages([]); // Clear existing images when creating new blog
    setMessage({ type: '', text: '' });
    
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  // Handle image upload for content
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('imageType', 'content');

        const response = await fetch(`${UPLOAD_API_URL}/image`, {
          method: 'POST',
          headers: getMultipartHeaders(`${UPLOAD_API_URL}/image`),
          body: formData
        });

        if (response.ok) {
          const result = await response.json();
          const newImage = {
            id: result.id,
            url: result.downloadUri,
            name: result.originalFilename,
            alt: result.altText || result.originalFilename.split('.')[0],
            size: Math.round(result.size / 1024) + ' KB',
            filename: result.filename,
            contentType: file.getContentType ? file.getContentType() : file.type,
            caption: result.caption,
            uploadedAt: result.uploadedAt,
            isExisting: false // Mark as newly uploaded
          };
          setImages(prev => [...prev, newImage]);
          setMessage({ type: 'success', text: 'Image uploaded and saved!' });
        } else {
          setMessage({ type: 'error', text: 'Failed to upload image: ' + file.name });
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        setMessage({ type: 'error', text: 'Error uploading image' });
      }
    }
  };

  // Handle featured image upload
  const handleFeaturedImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${UPLOAD_API_URL}/featured-image`, {
        method: 'POST',
        headers: getMultipartHeaders(`${UPLOAD_API_URL}/featured-image`),
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setBlogData(prev => ({
          ...prev,
          featuredImageUrl: result.downloadUri
        }));
        setMessage({ type: 'success', text: 'Featured image uploaded successfully!' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to upload featured image' });
      }
    } catch (error) {
      console.error('Error uploading featured image:', error);
      setMessage({ type: 'error', text: 'Error uploading featured image' });
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !blogData.tags.includes(tagInput.trim())) {
      setBlogData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setBlogData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setBlogData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug when title changes
    if (field === 'title' && !editingId) {
      generateSlug(value).then(slug => {
        setBlogData(prev => ({
          ...prev,
          slug: slug
        }));
      });
    }
  };

  // Filter blogs
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.author.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || blog.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const EditorToolbar = () => (
    <div className="editor-toolbar">
      <div className="toolbar-group">
        <select 
          onChange={(e) => formatText('fontSize', e.target.value)} 
          className="font-size-select"
          defaultValue="3"
        >
          <option value="1">8pt</option>
          <option value="2">10pt</option>
          <option value="3">12pt</option>
          <option value="4">14pt</option>
          <option value="5">18pt</option>
          <option value="6">24pt</option>
          <option value="7">36pt</option>
        </select>
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('bold')} className="toolbar-btn" type="button" title="Bold">
          <Bold size={16} />
        </button>
        <button onClick={() => formatText('italic')} className="toolbar-btn" type="button" title="Italic">
          <Italic size={16} />
        </button>
        <button onClick={() => formatText('underline')} className="toolbar-btn" type="button" title="Underline">
          <span style={{ textDecoration: 'underline' }}>U</span>
        </button>
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('formatBlock', 'h1')} className="toolbar-btn" type="button" title="Heading 1">
          <Heading1 size={16} />
        </button>
        <button onClick={() => formatText('formatBlock', 'h2')} className="toolbar-btn" type="button" title="Heading 2">
          <Heading2 size={16} />
        </button>
        <button onClick={() => formatText('formatBlock', 'h3')} className="toolbar-btn" type="button" title="Heading 3">
          H3
        </button>
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('insertUnorderedList')} className="toolbar-btn" type="button" title="Bullet List">
          <List size={16} />
        </button>
        <button onClick={() => formatText('insertOrderedList')} className="toolbar-btn" type="button" title="Numbered List">
          <span>1.</span>
        </button>
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('justifyLeft')} className="toolbar-btn" type="button" title="Align Left">
          <AlignLeft size={16} />
        </button>
        <button onClick={() => formatText('justifyCenter')} className="toolbar-btn" type="button" title="Align Center">
          <AlignCenter size={16} />
        </button>
      </div>
      
      <div className="toolbar-group">
        <input 
          type="color" 
          onChange={(e) => formatText('foreColor', e.target.value)}
          className="color-picker"
          title="Text Color"
        />
        <input 
          type="color" 
          onChange={(e) => formatText('hiliteColor', e.target.value)}
          className="color-picker"
          title="Highlight Color"
        />
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('createLink')} className="toolbar-btn" type="button" title="Insert Link">
          <Link size={16} />
        </button>
        <button onClick={() => setShowImageManager(true)} className="toolbar-btn" type="button" title="Insert Image">
          <Image size={16} />
        </button>
        <button 
          onClick={() => {
            // Direct PDF upload without modal (like WinNest)
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'application/pdf';
            input.multiple = false;
            input.onchange = handlePdfUpload;
            input.click();
          }}
          className="toolbar-btn" 
          type="button" 
          title="Insert PDF Document"
        >
          📄
        </button>
        <button onClick={() => formatText('formatBlock', 'blockquote')} className="toolbar-btn" type="button" title="Quote">
          <Quote size={16} />
        </button>
        <button onClick={() => formatText('formatBlock', 'pre')} className="toolbar-btn" type="button" title="Code Block">
          <Code size={16} />
        </button>
      </div>
      
      <div className="toolbar-group">
        <button onClick={() => formatText('removeFormat')} className="toolbar-btn" type="button" title="Clear Formatting">
          <span>🧹</span>
        </button>
        <button 
          onClick={() => {
            if (editorRef.current) {
              editorRef.current.innerHTML = '';
              setEditorContent('');
            }
          }} 
          className="toolbar-btn" 
          type="button"
          title="Clear All"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  const PdfManager = () => (
    <div className="image-manager-overlay">
      <div className="image-manager">
        <div className="image-manager-header">
          <h3>PDF Manager</h3>
          <button 
            onClick={() => setShowPdfManager(false)}
            className="close-btn"
            type="button"
          >
            ×
          </button>
        </div>
        
        <div className="image-upload-section">
          <label className="upload-btn">
            📄 Upload PDF
            <input
              type="file"
              accept=".pdf"
              multiple={false}
              onChange={handlePdfUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>

        <div className="image-grid">
          {pdfs.length === 0 && (
            <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
              No PDFs uploaded yet. Upload a PDF to get started.
            </p>
          )}
          {pdfs.map((pdf) => (
            <div key={pdf.id} className="image-item">
              <div className="pdf-preview">
                <div className="pdf-icon">📄</div>
                <div className="pdf-info">
                  <p className="pdf-name">{pdf.name}</p>
                  <p className="pdf-size">{pdf.size}</p>
                  <p className="pdf-pages">{pdf.pages} pages</p>
                </div>
              </div>
              <div className="pdf-actions">
                <button 
                  onClick={() => insertPdfIntoEditor(pdf)}
                  className="pdf-action-btn primary"
                  type="button"
                  title="Insert PDF into blog editor"
                >
                  📥 Insert PDF
                </button>
                <button 
                  onClick={() => {
                    if (pdf.downloadUrl) {
                      window.open(pdf.downloadUrl, '_blank');
                    } else {
                      console.log('Download URL not available');
                      setMessage({ type: 'error', text: 'PDF URL not available' });
                    }
                  }}
                  className="pdf-action-btn open"
                  type="button"
                  title="Open PDF in new tab"
                >
                  🔗 Open in New Tab
                </button>
                <button 
                  onClick={() => {
                    if (pdf.downloadUrl) {
                      // Create a link element and trigger download
                      const link = document.createElement('a');
                      link.href = pdf.downloadUrl;
                      link.download = pdf.name || 'document.pdf';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      setMessage({ type: 'success', text: 'PDF download started!' });
                    } else {
                      console.log('Download URL not available');
                      setMessage({ type: 'error', text: 'PDF URL not available for download' });
                    }
                  }}
                  className="pdf-action-btn download"
                  type="button"
                  title="Download PDF file"
                >
                  ⬇️ Download PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const ImageManager = () => (
    <div className="image-manager-overlay">
      <div className="image-manager">
        <div className="image-manager-header">
          <h3>Image Manager</h3>
          <button onClick={() => setShowImageManager(false)} className="close-btn">
            <X size={20} />
          </button>
        </div>
        
        <div className="image-upload-section">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            multiple
            style={{ display: 'none' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="upload-btn"
              type="button"
            >
              <Upload size={16} />
              Upload Images
            </button>
            {editingId && images.filter(img => img.isExisting).length > 0 && (
              <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold' }}>
                📸 {images.filter(img => img.isExisting).length} existing image(s) for this blog
              </div>
            )}
          </div>
        </div>
        
        <div className="image-gallery">
          {images.map(image => (
            <div key={image.id} className={`image-card ${image.isExisting ? 'existing-image' : 'new-image'}`}>
              {image.isExisting && (
                <div className="existing-badge">Already Added</div>
              )}
              <img src={image.url} alt={image.alt} />
              <div className="image-info">
                <p className="image-name">{image.name}</p>
                <p className="image-size">{image.size}</p>
                {image.isExisting && (
                  <p className="image-status">Previously uploaded for this blog</p>
                )}
              </div>
              <div className="image-actions">
                <button 
                  onClick={() => insertImageIntoEditor(image)}
                  className="action-btn primary"
                  type="button"
                >
                  Insert
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {images.length === 0 && (
          <div className="no-images">
            <p>
              {editingId 
                ? "This blog has no images yet. Click \"Upload Images\" to add some." 
                : "No images uploaded yet. Click \"Upload Images\" to add some."
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const BlogList = () => (
    <div className="blog-list">
      <div className="blog-list-header">
        <h2>All Siridharini Blogs ({filteredBlogs.length})</h2>
        <div className="blog-actions">
          <div className="search-bar">
            <Search size={16} />
            <input 
              type="text" 
              placeholder="Search Siridharini blogs..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select 
            className="status-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="PUBLISHED">Published</option>
            <option value="DRAFT">Draft</option>
            <option value="ARCHIVED">Archived</option>
          </select>
          <button 
            onClick={() => {
              resetForm();
              setActiveTab('editor');
            }}
            className="new-blog-btn"
            type="button"
          >
            <FileText size={16} />
            New Blog
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="loading">Loading Siridharini blogs...</div>
      ) : (
        <div className="blog-table">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Author</th>
                <th>Category</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map(blog => (
                <tr key={blog.id}>
                  <td>
                    <div>
                      <strong>{blog.title}</strong>
                      {blog.excerpt && (
                        <div className="blog-excerpt">{blog.excerpt.substring(0, 80)}...</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${blog.status.toLowerCase()}`}>
                      {blog.status}
                    </span>
                    {!blog.isActive && (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td>{blog.author}</td>
                  <td>{blog.category}</td>
                  <td>{new Date(blog.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="table-actions">
                      <button 
                        className="action-btn"
                        onClick={() => handleEdit(blog)}
                        title="Edit"
                        type="button"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => handleToggleStatus(blog.id)}
                        title={blog.isActive ? 'Deactivate' : 'Activate'}
                        type="button"
                      >
                        {blog.isActive ? <Eye size={14} /> : <AlertCircle size={14} />}
                      </button>
                      <button 
                        className="action-btn danger"
                        onClick={() => handleDelete(blog.id)}
                        title="Delete"
                        type="button"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredBlogs.length === 0 && !loading && (
            <div className="no-blogs">
              No Siridharini blogs found. {searchTerm || statusFilter ? 'Try adjusting your filters.' : 'Click "New Blog" to get started.'}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="blog-admin">
      <style>{`
        .blog-admin {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          background: #f8fafc;
          min-height: 100vh;
        }
        
        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .admin-title {
          font-size: 28px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .admin-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 30px;
        }
        
        .tab-btn {
          padding: 10px 20px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .tab-btn.active {
          background: #4A5D23;
          color: white;
          border-color: #4A5D23;
        }
        
        .tab-btn:hover {
          background: #f1f5f9;
        }
        
        .tab-btn.active:hover {
          background: #3a4a1a;
        }
        
        .message {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          font-weight: 500;
        }
        
        .message.success {
          background: #d1fae5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }
        
        .message.error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }
        
        .blog-editor {
          background: white;
          border-radius: 8px;
          padding: 30px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }
        
        .editor-title {
          font-size: 24px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .editor-actions {
          display: flex;
          gap: 10px;
        }
        
        .action-btn {
          padding: 8px 16px;
          border: 1px solid #e2e8f0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .action-btn.primary {
          background: #4A5D23;
          color: white;
          border-color: #4A5D23;
        }
        
        .action-btn.secondary {
          background: #f1f5f9;
          color: #64748b;
        }
        
        .action-btn.danger {
          background: #fee2e2;
          color: #dc2626;
          border-color: #fecaca;
        }
        
        .action-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .editor-form {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 30px;
        }
        
        .main-content {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .form-label {
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }
        
        .form-input {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 16px;
          transition: border-color 0.2s;
          direction: ltr;
          text-align: left;
        }
        
        .form-input:focus {
          outline: none;
          border-color: #4A5D23;
          box-shadow: 0 0 0 3px rgba(74, 93, 35, 0.1);
        }
        
        .form-textarea {
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 16px;
          resize: vertical;
          min-height: 100px;
          direction: ltr;
          text-align: left;
        }
        
        .form-textarea:focus {
          outline: none;
          border-color: #4A5D23;
          box-shadow: 0 0 0 3px rgba(74, 93, 35, 0.1);
        }
        
        .editor-toolbar {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          padding: 12px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-bottom: none;
          border-radius: 6px 6px 0 0;
          align-items: center;
        }
        
        .toolbar-group {
          display: flex;
          gap: 2px;
          padding-right: 12px;
          border-right: 1px solid #e2e8f0;
        }
        
        .toolbar-group:last-child {
          border-right: none;
        }
        
        .toolbar-btn {
          padding: 8px;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          min-width: 32px;
          height: 32px;
        }
        
        .toolbar-btn:hover {
          background: #e2e8f0;
          transform: translateY(-1px);
        }
        
        .toolbar-btn:active {
          transform: translateY(0);
        }
        
        .font-size-select {
          padding: 6px 8px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
          font-size: 12px;
          cursor: pointer;
          min-width: 60px;
        }
        
        .font-size-select:focus {
          outline: none;
          border-color: #4A5D23;
        }
        
        .color-picker {
          width: 32px;
          height: 32px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          background: transparent;
          padding: 0;
        }
        
        .color-picker::-webkit-color-swatch-wrapper {
          padding: 0;
        }
        
        .color-picker::-webkit-color-swatch {
          border: none;
          border-radius: 3px;
        }
        
        .content-editor {
          border: 1px solid #e2e8f0;
          border-radius: 0 0 6px 6px;
          min-height: 450px;
          padding: 20px;
          font-size: 16px;
          line-height: 1.6;
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
          writing-mode: horizontal-tb !important;
          background: white;
          color: #1e293b;
          overflow-y: auto;
        }
        
        .content-editor:focus {
          outline: none;
          border-color: #4A5D23;
          box-shadow: 0 0 0 3px rgba(74, 93, 35, 0.1);
        }
        
        .content-editor * {
          direction: ltr !important;
          text-align: left !important;
          unicode-bidi: normal !important;
          writing-mode: horizontal-tb !important;
        }
        
        .content-editor h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        
        .content-editor h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        
        .content-editor h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 1em 0;
        }
        
        .content-editor blockquote {
          border-left: 4px solid #4A5D23;
          padding-left: 16px;
          margin: 16px 0;
          font-style: italic;
          color: #6b7280;
        }
        
        .content-editor pre {
          background: #f1f5f9;
          border-radius: 4px;
          padding: 12px;
          overflow-x: auto;
          font-family: 'Courier New', monospace;
          font-size: 14px;
        }
        
        .content-editor ul,
        .content-editor ol {
          padding-left: 20px;
          margin: 16px 0;
        }
        
        .content-editor li {
          margin: 4px 0;
        }
        
        .content-editor a {
          color: #4A5D23;
          text-decoration: underline;
        }
        
        .content-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 12px;
          margin: 10px 0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transition: transform 0.2s ease;
        }
        
        .content-editor img:hover {
          transform: scale(1.02);
        }
        
        .content-editor figure {
          margin: 20px 0;
          text-align: center;
        }
        
        .content-editor figcaption {
          margin-top: 8px;
          font-size: 14px;
          color: #6b7280;
          font-style: italic;
        }
        
        .content-editor figcaption:focus {
          outline: 2px solid #4A5D23;
          outline-offset: 2px;
          border-radius: 4px;
        }
        
        /* PDF Content Styles */
        .pdf-content {
          padding: 20px;
          background: #f9fafb;
          border-radius: 8px;
          margin: 20px 0;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        .pdf-content h2 {
          color: #1f2937;
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 16px;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 8px;
        }
        
        .pdf-content h3 {
          color: #374151;
          font-size: 18px;
          font-weight: 600;
          margin: 16px 0 8px 0;
        }
        
        .pdf-content p {
          color: #4b5563;
          line-height: 1.6;
          margin: 8px 0;
          padding: 4px 0;
        }
        
        .pdf-content hr {
          border: none;
          border-top: 1px solid #d1d5db;
          margin: 16px 0;
        }
        
        .content-editor .pdf-content {
          max-width: 100%;
          overflow-wrap: break-word;
        }
        
        .pdf-table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .pdf-table td {
          padding: 12px;
          border: 1px solid #e5e7eb;
          color: #374151;
          font-size: 14px;
        }
        
        .pdf-table tr:nth-child(even) {
          background: #f9fafb;
        }
        
        .pdf-table tr:hover {
          background: #f3f4f6;
        }
        
        /* Blog PDF Embed Styles */
        .blog-pdf-embed {
          margin: 20px 0;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          position: relative;
        }
        
        .blog-pdf-embed iframe {
          width: 100%;
          height: 600px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: white;
        }
        
        .content-editor .blog-pdf-embed {
          user-select: none;
          pointer-events: none;
        }
        
        .content-editor .blog-pdf-embed a,
        .content-editor .blog-pdf-embed button {
          pointer-events: auto;
        }
        
        .sidebar {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        
        .sidebar-section {
          background: #f8fafc;
          border-radius: 8px;
          padding: 20px;
          border: 1px solid #e2e8f0;
        }
        
        .sidebar-title {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 15px;
        }
        
        .featured-image {
          width: 100%;
          height: 200px;
          border: 2px dashed #d1d5db;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          cursor: pointer;
          transition: all 0.2s;
          background-size: cover;
          background-position: center;
          position: relative;
        }
        
        .featured-image:hover {
          border-color: #4A5D23;
          background-color: #f8fafc;
        }
        
        .featured-image.has-image {
          border: none;
        }
        
        .featured-image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          color: white;
          opacity: 0;
          transition: opacity 0.2s;
          border-radius: 6px;
        }
        
        .featured-image:hover .featured-image-overlay {
          opacity: 1;
        }
        
        .tag-input-container {
          display: flex;
          gap: 8px;
          margin-bottom: 10px;
        }
        
        .tag-input {
          flex: 1;
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          direction: ltr;
          text-align: left;
        }
        
        .tag-input:focus {
          outline: none;
          border-color: #4A5D23;
          box-shadow: 0 0 0 3px rgba(74, 93, 35, 0.1);
        }
        
        .add-tag-btn {
          padding: 8px 12px;
          background: #4A5D23;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: background 0.2s;
        }
        
        .add-tag-btn:hover {
          background: #3a4a1a;
        }
        
        .tags-container {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .tag {
          background: #e0e7ff;
          color: #3730a3;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        
        .tag-remove {
          cursor: pointer;
          color: #6b7280;
          transition: color 0.2s;
        }
        
        .tag-remove:hover {
          color: #dc2626;
        }
        
        .status-select {
          padding: 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          direction: ltr;
          text-align: left;
          width: 100%;
        }
        
        .status-select:focus {
          outline: none;
          border-color: #4A5D23;
          box-shadow: 0 0 0 3px rgba(74, 93, 35, 0.1);
        }
        
        .image-manager-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .image-manager {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 900px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        
        .image-manager-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        
        .image-manager-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          color: #6b7280;
          border-radius: 4px;
          transition: all 0.2s;
        }
        
        .close-btn:hover {
          background: #f1f5f9;
          color: #374151;
        }
        
        .image-upload-section {
          padding: 20px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        
        .upload-btn {
          padding: 12px 20px;
          background: #4A5D23;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }
        
        .upload-btn:hover {
          background: #3a4a1a;
          transform: translateY(-1px);
        }
        
        .image-gallery {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 16px;
          padding: 20px;
        }
        
        .image-card {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          background: white;
          transition: all 0.2s;
        }
        
        .image-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateY(-2px);
        }
        
        .image-card img {
          width: 100%;
          height: 140px;
          object-fit: cover;
        }
        
        .image-info {
          padding: 12px;
        }
        
        .image-name {
          font-size: 13px;
          font-weight: 500;
          margin: 0 0 4px 0;
          color: #1e293b;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .image-size {
          font-size: 11px;
          color: #6b7280;
          margin: 0;
        }
        
        .image-actions {
          display: flex;
          gap: 8px;
          padding: 12px;
          border-top: 1px solid #f1f5f9;
        }
        
        .no-images {
          text-align: center;
          padding: 40px;
          color: #6b7280;
        }
        
        .blog-list {
          background: white;
          border-radius: 8px;
          padding: 30px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .blog-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }
        
        .blog-list-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
        }
        
        .blog-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }
        
        .search-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          min-width: 200px;
        }
        
        .search-bar input {
          border: none;
          outline: none;
          background: transparent;
          direction: ltr;
          text-align: left;
          flex: 1;
        }
        
        .new-blog-btn {
          background: #4A5D23;
          color: white;
          border-color: #4A5D23;
        }
        
        .new-blog-btn:hover {
          background: #3a4a1a;
          transform: translateY(-1px);
        }
        
        .blog-table {
          overflow-x: auto;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
        }
        
        .blog-table table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .blog-table th,
        .blog-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .blog-table th {
          font-weight: 600;
          color: #374151;
          background: #f8fafc;
        }
        
        .blog-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .blog-excerpt {
          font-size: 12px;
          color: #6b7280;
          margin-top: 4px;
        }
        
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          margin-right: 4px;
        }
        
        .status-badge.published {
          background: #d1fae5;
          color: #065f46;
        }
        
        .status-badge.draft {
          background: #fef3c7;
          color: #92400e;
        }
        
        .status-badge.archived {
          background: #f3f4f6;
          color: #374151;
        }
        
        .status-badge.inactive {
          background: #fee2e2;
          color: #991b1b;
        }
        
        .table-actions {
          display: flex;
          gap: 8px;
        }
        
        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
          font-size: 16px;
          color: #6b7280;
        }
        
        .no-blogs {
          text-align: center;
          padding: 40px;
          color: #6b7280;
          font-size: 16px;
        }

        .pdf-preview {
          display: flex;
          align-items: center;
          padding: 10px;
          background: #f5f5f5;
          border-radius: 8px;
          margin-bottom: 10px;
        }
        
        .pdf-icon {
          font-size: 2rem;
          margin-right: 15px;
          color: #e74c3c;
        }
        
        .pdf-info {
          flex: 1;
        }
        
        .pdf-actions {
          display: flex;
          gap: 8px;
          flex-direction: column;
          margin-top: 10px;
        }
        
        .pdf-action-btn {
          padding: 8px 12px;
          border-radius: 6px;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        
        .pdf-action-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }
        
        .pdf-action-btn.primary {
          background: #4A5D23;
          color: white;
        }
        
        .pdf-action-btn.primary:hover {
          background: #3a4a1b;
        }
        
        .pdf-action-btn.open {
          background: #3b82f6;
          color: white;
        }
        
        .pdf-action-btn.open:hover {
          background: #2563eb;
        }
        
        .pdf-action-btn.download {
          background: #22c55e;
          color: white;
        }
        
        .pdf-action-btn.download:hover {
          background: #16a34a;
        }
        
        .pdf-name {
          font-weight: 600;
          margin: 0 0 5px 0;
          font-size: 14px;
        }
        
        .pdf-size, .pdf-pages {
          margin: 0;
          font-size: 12px;
          color: #666;
        }

        .image-item {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          background: white;
        }
        
        @media (max-width: 1024px) {
          .editor-form {
            grid-template-columns: 1fr;
          }
          
          .toolbar-group {
            border-right: none;
            padding-right: 0;
          }
        }
        
        @media (max-width: 768px) {
          .admin-header {
            flex-direction: column;
            gap: 15px;
          }
          
          .blog-actions {
            flex-direction: column;
            gap: 10px;
          }
          
          .editor-actions {
            flex-wrap: wrap;
          }
          
          .admin-tabs {
            flex-wrap: wrap;
          }
          
          .image-gallery {
            grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          }
          
          .search-bar {
            min-width: 150px;
          }
        }
        
        /* Existing vs New image indicators */
        .image-card.existing-image {
          border: 2px solid #10b981 !important;
          background-color: #f0fdf4;
        }
        
        .image-card.new-image {
          border: 2px solid #3b82f6 !important;
          background-color: #eff6ff;
        }
        
        .existing-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #10b981;
          color: white;
          font-size: 10px;
          font-weight: bold;
          padding: 2px 6px;
          border-radius: 4px;
          text-transform: uppercase;
          z-index: 1;
        }
        
        .image-card {
          position: relative;
          border-radius: 8px;
        }
        
        .image-status {
          font-size: 11px;
          color: #10b981;
          font-style: italic;
          margin: 2px 0 0 0;
        }

        @media (max-width: 640px) {
          .blog-admin {
            padding: 10px;
          }
          
          .blog-editor {
            padding: 20px;
          }
          
          .image-gallery {
            grid-template-columns: 1fr;
          }
          
          .blog-actions {
            width: 100%;
          }
          
          .search-bar {
            min-width: 100%;
          }
        }
      `}</style>
      
      <div className="admin-header">
        <h1 className="admin-title">SiriDharini Blog Management</h1>
        <div className="editor-actions">
          <button className="action-btn" type="button">
            <Settings size={16} />
            Settings
          </button>
          <button className="action-btn primary" type="button">
            <User size={16} />
            Profile
          </button>
        </div>
      </div>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => {
            console.log('🔄 Switching to blog list view');
            if (editingId) {
              // If editing, navigate to clear the URL
              navigate('/adminportal/siridharini-blog-upload');
            } else {
              setActiveTab('list');
            }
            resetForm(); // Ensure form is reset when going to blog list
          }}
          type="button"
        >
          <FileText size={16} />
          All Blogs
        </button>
        <button 
          className={`tab-btn ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => {
            console.log('📝 Switching to create blog mode');
            if (editingId) {
              // If currently editing, navigate to base URL to create new
              navigate('/adminportal/siridharini-blog-upload');
            } else {
              setActiveTab('editor');
            }
            resetForm();
          }}
          type="button"
        >
          <Edit3 size={16} />
          {editingId ? 'Edit Blog' : 'Create Blog'}
        </button>
      </div>
      
      {activeTab === 'list' ? (
        <BlogList />
      ) : (
        <div className="blog-editor">
          <div className="editor-header">
            <h2 className="editor-title">{editingId ? 'Edit Siridharini Blog' : 'Create New Siridharini Blog'}</h2>
            <div className="editor-actions">
              <button 
                className="action-btn secondary"
                onClick={() => {
                  console.log('❌ Cancel button clicked - returning to blog list');
                  resetForm();
                  navigate('/adminportal/siridharini-blog-upload');
                }}
                type="button"
              >
                <X size={16} />
                Cancel
              </button>
              <button 
                className="action-btn"
                onClick={() => handleSubmit('DRAFT')}
                disabled={submitting}
                type="button"
              >
                <Save size={16} />
                {submitting ? 'Saving...' : 'Save Draft'}
              </button>
              <button 
                className="action-btn primary"
                onClick={() => handleSubmit('PUBLISHED')}
                disabled={submitting}
                type="button"
              >
                <Globe size={16} />
                {submitting ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
          
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="editor-form">
              <div className="main-content">
                <div className="form-group">
                  <label className="form-label">Blog Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter blog title..."
                    value={blogData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">URL Slug</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="blog-url-slug"
                    value={blogData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Excerpt</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Brief description of the blog..."
                    value={blogData.excerpt}
                    onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Content *</label>
                  <EditorToolbar />
                  <div
                    ref={editorRef}
                    className="content-editor"
                    contentEditable
                    suppressContentEditableWarning={true}
                    onInput={handleEditorInput}
                    onFocus={handleEditorFocus}
                    onKeyDown={handleEditorKeyDown}
                    onPaste={handleEditorPaste}
                    dir="ltr"
                    style={{
                      direction: 'ltr',
                      textAlign: 'left',
                      unicodeBidi: 'normal',
                      writingMode: 'horizontal-tb'
                    }}
                  />
                </div>
              </div>
              
              <div className="sidebar">
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Publish Settings</h3>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select 
                      className="status-select"
                      value={blogData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <option value="DRAFT">Draft</option>
                      <option value="PUBLISHED">Published</option>
                      <option value="ARCHIVED">Archived</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Author</label>
                    <input
                      type="text"
                      className="form-input"
                      value={blogData.author}
                      onChange={(e) => handleInputChange('author', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-input"
                      value={blogData.displayOrder}
                      onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 0)}
                      min="0"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        checked={blogData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      Active
                    </label>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        checked={blogData.isFeatured}
                        onChange={(e) => handleInputChange('isFeatured', e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      Featured
                    </label>
                  </div>
                </div>
                
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Featured Image</h3>
                  {blogData.featuredImageUrl && (
                    <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold', marginBottom: '5px' }}>
                      ✅ Featured image already uploaded for this blog
                    </div>
                  )}
                  <input
                    type="file"
                    ref={featuredImageRef}
                    onChange={handleFeaturedImageUpload}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <div 
                    className={`featured-image ${blogData.featuredImageUrl ? 'has-image' : ''}`}
                    onClick={() => featuredImageRef.current?.click()}
                    style={blogData.featuredImageUrl ? { backgroundImage: `url(${blogData.featuredImageUrl})` } : {}}
                  >
                    {!blogData.featuredImageUrl ? (
                      <>
                        <Upload size={24} color="#6b7280" />
                        <p>Click to upload featured image</p>
                      </>
                    ) : (
                      <div className="featured-image-overlay">
                        <Upload size={24} />
                        <p>Change Image</p>
                      </div>
                    )}
                  </div>
                  {!blogData.featuredImageUrl && editingId && (
                    <div style={{ fontSize: '11px', color: '#6b7280', fontStyle: 'italic', marginTop: '5px' }}>
                      No featured image found for this blog
                    </div>
                  )}
                </div>
                
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Categories & Tags</h3>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select 
                      className="status-select"
                      value={blogData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                    >
                      <option value="">Select Category</option>
                      <option value="INVESTMENT">Investment</option>
                      <option value="FORESTRY">Forestry</option>
                      <option value="SERVICES">Services</option>
                      <option value="DOCUMENTS">Documents</option>
                      <option value="NEWS">News</option>
                      <option value="TIPS">Tips & Guides</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Tags</label>
                    <div className="tag-input-container">
                      <input
                        type="text"
                        className="tag-input"
                        placeholder="Add tag..."
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      />
                      <button className="add-tag-btn" onClick={addTag} type="button">
                        <Tag size={14} />
                      </button>
                    </div>
                    <div className="tags-container">
                      {blogData.tags.map(tag => (
                        <span key={tag} className="tag">
                          {tag}
                          <span 
                            className="tag-remove" 
                            onClick={() => removeTag(tag)}
                          >
                            <X size={12} />
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="sidebar-section">
                  <h3 className="sidebar-title">SEO Settings</h3>
                  <div className="form-group">
                    <label className="form-label">SEO Title</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="SEO optimized title..."
                      value={blogData.seoTitle}
                      onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Meta Description</label>
                    <textarea
                      className="form-textarea"
                      placeholder="SEO meta description..."
                      value={blogData.seoDescription}
                      onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label">Read Time</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g., 5 MIN READ"
                      value={blogData.readTime}
                      onChange={(e) => handleInputChange('readTime', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
      
      {showImageManager && <ImageManager />}
      {showPdfManager && <PdfManager />}
    </div>
  );
};

export default SiridhariniBlogAdminUpload;